// ---------------------------------------------------------------------------
// Structure effects: where each structure type's in-war effect plugs in
// ---------------------------------------------------------------------------
//
// Structure types live in the pacing sheet (progression/pacing/structures.csv);
// what each one DOES in a war lives here, keyed by the same name. Adding,
// removing or swapping a structure's effect is an edit to this table (and the
// effect's own code), never to the engine. A test fails if the sheet names a
// structure this table doesn't.
//
// An entry is { kind, ...hooks }:
//   kind 'extractor'  multiplies its lot type's network. That effect is pure
//                     data (the sheet's extractor column) and lives in the
//                     yield rule itself (subplotEconomy.lotYield).
//   kind 'converter'  refines raw resources into a refined one between wars,
//                     behind the converter switch (not built yet).
//   kind 'stub'       no effect yet: the structure stands, counts toward
//                     kills and Hive Level, and does nothing else.
//
// Hooks the war calls (all optional; a stub has none):
//   roundYield({ homefront, structure }) → extra yield this round, credited
//       with the lot yield after the round resolves, while the structure
//       stands.
// New hook points are added here, and called from the engine, as the
// redesigned roster needs them.

const EXTRACTOR = Object.freeze({ kind: 'extractor' })
const CONVERTER = Object.freeze({ kind: 'converter' })
const STUB = Object.freeze({ kind: 'stub' })

export const STRUCTURE_EFFECTS = Object.freeze({
    'Lumber Mill': EXTRACTOR,
    Mine: EXTRACTOR,
    Colliery: EXTRACTOR,
    Well: EXTRACTOR,
    Derrick: EXTRACTOR,
    'Silicon Pit': EXTRACTOR,
    'Sand Pit': EXTRACTOR,
    Smelter: CONVERTER,
    'Cement Works': CONVERTER,
    Vault: STUB,
    Warehouse: STUB,
    Watchtower: STUB,
    'Rocket Pad': STUB,
    Jammer: STUB,
    Depot: STUB,        // its old effect depended on roads, which are gone
    Bunker: STUB,
    Foundry: STUB,
})

// The effect for a war structure (by its type name), or null for a structure
// with no type (a Hive from before rules v8).
export function effectOf(structure) {
    return structure?.name ? STRUCTURE_EFFECTS[structure.name] ?? null : null
}

// The sum a hook returns across the Hive's standing structures.
export function sumHook(hook, homefront, isStanding) {
    let total = 0
    for (const st of Object.values(homefront?.structures ?? {})) {
        const fn = effectOf(st)?.[hook]
        if (fn && isStanding(st)) total += fn({ homefront, structure: st }) || 0
    }
    return total
}
